import { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Card,
  CardContent,
  Button,
  Box,
  Tabs,
  Tab,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import Iconify from '../../components/iconify';
import BreadCrumb from '../../components/breadcrumb/BreadCrumbNavigation';
import Loading from '../../layouts/loading';
import { CompOffRequestForm, CompOffTable, CompOffFilters } from './components';
import {
  createCompOffRequest,
  getMyCompOffRequests,
  getAllCompOffRequests,
  cancelCompOffRequest,
  updateCompOffRequest,
  getCompOffBalance,
} from '../../services/compOffService';
import { isAdmin, isManager, isHR, checkComponentPermission } from '../../utils/share';

const queryClient = new QueryClient();

function CompOffContent() {
  const [openFormModal, setOpenFormModal] = useState(false);
  const [editingCompOff, setEditingCompOff] = useState(null);
  const isAdminUser = isAdmin();
  const isManagerUser = isManager();
  const isHRUser = isHR();
  const [activeTab, setActiveTab] = useState(() => ((isManagerUser || isHRUser) && !isAdminUser ? 1 : 0));
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelingId, setCancelingId] = useState(null);
  const user = useSelector((state) => state?.login?.user || null);
  const userId = user?.id;
  const isAdminOrManagerOrHR = isAdminUser || isManagerUser || isHRUser;

  const queryClientInstance = useQueryClient();

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      if (value === undefined || value === '') {
        delete newFilters[key];
      } else {
        newFilters[key] = value;
      }
      setPagination((prev) => ({ ...prev, page: 1 }));
      return newFilters;
    });
  };

  const handleResetFilters = () => {
    setFilters({});
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPagination({ page: 1, pageSize: newPageSize });
  };

  const { data: balanceData } = useQuery({
    queryKey: ['compOffBalance'],
    queryFn: async () => {
      try {
        const response = await getCompOffBalance();
        return response?.data?.data || { totalCredits: 0 };
      } catch (error) {
        return { totalCredits: 0 };
      }
    },
    enabled: activeTab === 0,
  });

  const totalCredits = balanceData?.totalCredits ?? 0;

  const { data: myData = { rows: [], count: 0 }, isLoading: isLoadingMy } = useQuery({
    queryKey: ['myCompOffRequests', filters, pagination],
    queryFn: async () => {
      try {
        const response = await getMyCompOffRequests(filters, pagination);
        return response?.data?.data || { rows: [], count: 0 };
      } catch (error) {
        toast.error('Error fetching your comp-off requests');
        return { rows: [], count: 0 };
      }
    },
  });

  const myRequests = myData.rows || [];
  const myCount = myData.count || 0;

  const { data: allData = { rows: [], count: 0 }, isLoading: isLoadingAll } = useQuery({
    queryKey: ['allCompOffRequests', filters, pagination],
    queryFn: async () => {
      try {
        const response = await getAllCompOffRequests(filters, pagination);
        return response?.data?.data || { rows: [], count: 0 };
      } catch (error) {
        toast.error('Error fetching all comp-off requests');
        return { rows: [], count: 0 };
      }
    },
    enabled: isAdminOrManagerOrHR,
  });

  const allRequests = allData.rows || [];
  const allCount = allData.count || 0;

  const invalidateQueries = () => {
    queryClientInstance.invalidateQueries(['myCompOffRequests']);
    queryClientInstance.invalidateQueries(['compOffBalance']);
    if (isAdminOrManagerOrHR) {
      queryClientInstance.invalidateQueries(['allCompOffRequests']);
    }
  };

  const createMutation = useMutation({
    mutationFn: createCompOffRequest,
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Comp-off request submitted successfully');
      setOpenFormModal(false);
      setEditingCompOff(null);
      invalidateQueries();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error submitting comp-off request');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateCompOffRequest(id, data),
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Comp-off request updated successfully');
      setOpenFormModal(false);
      setEditingCompOff(null);
      invalidateQueries();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error updating comp-off request');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: cancelCompOffRequest,
    onSuccess: (response) => {
      toast.success(response?.data?.message || 'Comp-off request deleted successfully');
      invalidateQueries();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Error deleting comp-off request');
    },
  });

  const handleCreateClick = () => {
    setEditingCompOff(null);
    setOpenFormModal(true);
  };

  const handleEditCompOff = useCallback(
    (compOffId) => {
      const toEdit =
        activeTab === 0 ? myRequests.find((r) => r.id === compOffId) : allRequests.find((r) => r.id === compOffId);
      if (toEdit) {
        setEditingCompOff(toEdit);
        setOpenFormModal(true);
      }
    },
    [activeTab, myRequests, allRequests]
  );

  const handleFormModalClose = useCallback(() => {
    setOpenFormModal(false);
    setEditingCompOff(null);
  }, []);

  const handleFormSubmit = useCallback(
    async (values) => {
      if (editingCompOff) {
        await updateMutation.mutateAsync({ id: editingCompOff.id, data: values });
      } else {
        await createMutation.mutateAsync(values);
      }
      return true;
    },
    [createMutation, updateMutation, editingCompOff]
  );

  const handleCancelCompOff = useCallback((id) => {
    setCancelingId(id);
    setCancelDialogOpen(true);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    if (cancelingId) {
      cancelMutation.mutate(cancelingId);
      setCancelDialogOpen(false);
      setCancelingId(null);
    }
  }, [cancelingId, cancelMutation]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const effectiveTab = activeTab;
  const isLoading = effectiveTab === 0 ? isLoadingMy : isLoadingAll;
  const currentRequests = effectiveTab === 0 ? myRequests : allRequests;
  const currentCount = effectiveTab === 0 ? myCount : allCount;

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Comp-Off', path: '' },
  ];

  return (
    <>
      <Helmet>
        <title> Comp-Off </title>
      </Helmet>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="flex-end" mb={3}>
          <Stack direction="row" spacing={2} alignItems="center">
            {effectiveTab === 0 && checkComponentPermission('CREATE_COMP_OFF') && (
              <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={handleCreateClick}>
                Request Comp-Off
              </Button>
            )}
          </Stack>
        </Stack>

        {effectiveTab === 0 && (
          <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    Total Comp-Off Credits
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {totalCredits} {totalCredits === 1 ? 'day' : 'days'}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.75, mt: 0.5, display: 'block' }}>
                    Approved credits considered by HR during monthly salary processing
                  </Typography>
                </Box>
                <Tooltip title="Total approved compensatory off days earned by working on weekends or holidays">
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 32,
                    }}
                  >
                    <CalendarToday sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                </Tooltip>
              </Stack>
            </CardContent>
          </Card>
        )}

        {isAdminOrManagerOrHR ? (
          <Card sx={{ mb: 3 }}>
            <Tabs value={effectiveTab} onChange={handleTabChange}>
              <Tab label="My Comp-Off" />
              <Tab label="All Comp-Off" />
            </Tabs>
          </Card>
        ) : (
          <Card sx={{ mb: 3 }}>
            <Tabs value={0} sx={{ pointerEvents: 'none' }}>
              <Tab label="My Comp-Off" />
            </Tabs>
          </Card>
        )}

        <CompOffFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          showEmployeeFilter={effectiveTab === 1 && isAdminOrManagerOrHR}
        />

        <Loading open={isLoading} />
        <Card>
          <Box sx={{ p: 3 }}>
            <CompOffTable
              compOffRequests={currentRequests}
              onCancelCompOff={handleCancelCompOff}
              onEditCompOff={handleEditCompOff}
              showEmployee={effectiveTab === 1}
              currentUserId={userId}
              count={currentCount}
              page={pagination.page - 1}
              rowsPerPage={pagination.pageSize}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>

        {openFormModal && (
          <CompOffRequestForm
            open
            onClose={handleFormModalClose}
            onSubmit={handleFormSubmit}
            compOffData={editingCompOff}
            isEdit={!!editingCompOff}
          />
        )}

        <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
          <DialogTitle>Delete Comp-Off Request</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this comp-off request? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)}>No</Button>
            <LoadingButton
              variant="contained"
              color="error"
              onClick={handleConfirmCancel}
              loading={cancelMutation.isLoading}
            >
              Yes, Delete
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}

export default function CompOffPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <CompOffContent />
    </QueryClientProvider>
  );
}
